import { TanStackDevtools } from '@tanstack/react-devtools';
import { HeadContent, Scripts, createRootRoute } from '@tanstack/react-router';
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools';
import { ShopifyStoreProvider } from '../context/store-context';
import aosCss from '@reimujs/aos/dist/aos.css?url';
import appCss from '../styles/styles.scss?url';
import { notFoundShellPath, siteMetadata } from '../types/meta';
import { themeAttribute, themeStorageKey } from '../types/theme';
import { notFoundSeo, seo, toAbsoluteUrl } from '../utils/seo';

// icons in public/icons (generated by gatsby-plugin-manifest in the old project)
const appleTouchIconSizes = [48, 72, 96, 144, 192, 256, 384, 512];

// Applies the saved theme before the first paint, so dark mode doesn't flash light while the page loads
// (the layout keeps it in sync afterwards, see routes/_layout.tsx).
const themeInitScript = `(function () {
  try {
    var theme = JSON.parse(localStorage.getItem(${JSON.stringify(themeStorageKey)}));
    if (theme === 'light' || theme === 'dark') {
      document.documentElement.setAttribute(${JSON.stringify(themeAttribute)}, theme);
    }
  } catch (e) {}
})();`;

export const Route = createRootRoute({
  // https://tanstack.com/router/latest/docs/guide/document-head-management#managing-the-document-head
  // Site-wide defaults: a page's head() only overrides what differs (see utils/seo.ts)
  head: ({ matches }) => {
    const pathname = matches.at(-1)?.pathname ?? '/';
    const url = toAbsoluteUrl(pathname);
    // the 404 page (SPA shell, served at any unknown URL, so it has no URL of its own)
    const isShell = pathname === notFoundShellPath;
    // The 404 page: the shell, an unknown URL (marked on a match: `_notFound`, called `globalNotFound` before
    // @tanstack/router-core 1.171) or a loader throwing notFound() (e.g. product.$handle.tsx, its head sets the same
    // tags).
    // Pending matches: the shell is starting up at an unknown URL (prerendered pages and in-app navigations give
    // head() loaded matches), e.g. the collection loader of /unknown hasn't thrown notFound() yet. The first client
    // render has to produce the shell's tags: the shell's tags it doesn't produce would stay in the page for good
    // (e.g. a second robots noindex, kept after navigating to a real page).
    const isNotFound =
      isShell ||
      matches.some(
        match =>
          match._notFound ||
          match.status === 'notFound' ||
          match.status === 'pending'
      );

    return {
      meta: [
        { charSet: 'utf-8' },
        { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        { name: 'theme-color', content: siteMetadata.themeColor },
        ...seo({
          description: siteMetadata.description,
          image: siteMetadata.image
        }),
        { property: 'og:type', content: 'website' },
        // a 404 page has no URL of its own
        ...(isNotFound ? [] : [{ property: 'og:url', content: url }]),
        { property: 'og:site_name', content: siteMetadata.title },
        { property: 'og:locale', content: siteMetadata.locale },
        { name: 'twitter:card', content: 'summary_large_image' },
        // later tags of the same route win too
        ...(isNotFound ? notFoundSeo() : [])
      ],
      links: [
        ...(isNotFound ? [] : [{ rel: 'canonical', href: url }]),
        { rel: 'icon', href: '/favicon-32x32.png', type: 'image/png' },
        { rel: 'manifest', href: '/manifest.webmanifest' },
        ...appleTouchIconSizes.map(size => ({
          rel: 'apple-touch-icon',
          sizes: `${size}x${size}`,
          href: `/icons/icon-${size}x${size}.png`
        })),
        // Montserrat (used to be added in gatsby-ssr.tsx)
        { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
        {
          rel: 'preconnect',
          href: 'https://fonts.gstatic.com',
          crossOrigin: 'anonymous'
        },
        {
          rel: 'stylesheet',
          href: 'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap'
        },
        // scroll animations (see routes/_layout.tsx)
        { rel: 'stylesheet', href: aosCss },
        { rel: 'stylesheet', href: appCss }
      ]
    };
  },
  // https://tanstack.com/router/latest/docs/guide/document-head-management#managing-body-scripts
  scripts: () => [
    {
      src: 'https://code.jquery.com/jquery-3.7.1.min.js',
      integrity: 'sha256-/JqT3SQfawRcv/BIHPThkBvs0OEvtFFmqPF/lYI/Cxo=',
      crossOrigin: 'anonymous'
    },
    {
      src: 'https://cdn1.stamped.io/files/widget.min.js'
    }
  ],
  shellComponent: RootDocument
});

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="hu" suppressHydrationWarning>
      <head>
        <script dangerouslySetInnerHTML={{ __html: themeInitScript }} />
        <HeadContent />
      </head>
      <body>
        <ShopifyStoreProvider>{children}</ShopifyStoreProvider>
        <TanStackDevtools
          config={{
            position: 'bottom-right'
          }}
          plugins={[
            {
              name: 'Tanstack Router',
              render: <TanStackRouterDevtoolsPanel />
            }
          ]}
        />
        <Scripts />
      </body>
    </html>
  );
}
